import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import {
  fetchAdminUserByEmail,
  resolveAdminAccess,
  supabase,
} from "@/lib/supabase";
import { getErrorMessage } from "@/lib/utils";
import type { User } from "@/types/database";

type AuthContextValue = {
  session: Session | null;
  isAdmin: boolean;
  adminProfile: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<string | null>;
  signOut: () => Promise<void>;
  verifyPassword: (password: string) => Promise<string | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

async function loadAdminState(session: Session | null) {
  if (!session?.user) {
    return { isAdmin: false, adminProfile: null as User | null };
  }

  const isAdmin = await resolveAdminAccess(session.user);
  if (!isAdmin) {
    return { isAdmin: false, adminProfile: null as User | null };
  }

  const adminProfile = session.user.email
    ? await fetchAdminUserByEmail(session.user.email).catch(() => null)
    : null;

  return { isAdmin: true, adminProfile };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminProfile, setAdminProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback(async (nextSession: Session | null) => {
    try {
      setSession(nextSession);

      if (!nextSession?.user) {
        setIsAdmin(false);
        setAdminProfile(null);
        return;
      }

      const { isAdmin: nextIsAdmin, adminProfile: nextProfile } = await loadAdminState(nextSession);

      if (!nextIsAdmin) {
        await supabase.auth.signOut();
        setSession(null);
        setIsAdmin(false);
        setAdminProfile(null);
        return;
      }

      setIsAdmin(true);
      setAdminProfile(nextProfile);
    } catch (error) {
      console.error("Failed to apply auth session:", error);
      await supabase.auth.signOut().catch(() => undefined);
      setSession(null);
      setIsAdmin(false);
      setAdminProfile(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    supabase.auth
      .getSession()
      .then(async ({ data: { session: initialSession } }) => {
        if (!mounted) return;
        await applySession(initialSession);
      })
      .catch((error) => {
        console.error("Failed to restore auth session:", error);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      void applySession(nextSession).finally(() => {
        if (mounted) setLoading(false);
      });
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [applySession]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        if (error.message.toLowerCase().includes("invalid login credentials")) {
          return "Invalid email or password.";
        }
        return error.message;
      }

      if (!data.session?.user) {
        return "Sign in failed. Please try again.";
      }

      const isAllowed = await resolveAdminAccess(data.session.user);
      if (!isAllowed) {
        await supabase.auth.signOut();
        return "Access denied. This account is not authorized for Overwatch.";
      }

      await applySession(data.session);
      return null;
    } catch (error) {
      console.error("Sign in failed:", error);
      return getErrorMessage(error, "Sign in failed. Please try again.");
    }
  }, [applySession]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Sign out failed:", error);
    } finally {
      setSession(null);
      setIsAdmin(false);
      setAdminProfile(null);
    }
  }, []);

  const verifyPassword = useCallback(
    async (password: string): Promise<string | null> => {
      const email = session?.user?.email;
      if (!email) {
        return "You must be signed in to confirm changes.";
      }

      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          if (error.message.toLowerCase().includes("invalid login credentials")) {
            return "Incorrect password.";
          }
          return error.message;
        }

        if (!data.session?.user) {
          return "Password verification failed. Please try again.";
        }

        setSession(data.session);
        return null;
      } catch (error) {
        console.error("Password verification failed:", error);
        return getErrorMessage(error, "Password verification failed. Please try again.");
      }
    },
    [session?.user?.email]
  );

  const value = useMemo(
    () => ({ session, isAdmin, adminProfile, loading, signIn, signOut, verifyPassword }),
    [session, isAdmin, adminProfile, loading, signIn, signOut, verifyPassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
